'use client';

import { Button } from '@/components/Buttons/Button/Button';
import React from 'react';

export interface ErrorStateProps {
  /** Main heading shown to the user. */
  title?: string;

  /** Supporting message shown under the heading. */
  message?: string;

  /**
   * Next.js error digest hash. When present it is displayed in small text so the
   * user can quote it to support and it can be correlated with server/App Insights logs.
   */
  digest?: string;

  /** Label for the primary (retry) action. */
  retryLabel?: string;

  /** Handler for the primary (retry) action. When omitted, the retry button is hidden. */
  onRetry?: () => void;

  /** Label for the secondary action. */
  secondaryLabel?: string;

  /** Handler for the secondary action. When omitted, the secondary button is hidden. */
  onSecondary?: () => void;
}

/**
 * ErrorState
 *
 * Presentational fallback UI shared by the App Router error boundaries
 * (`app/error.tsx`, `app/global-error.tsx`, and route-level boundaries).
 *
 * Design notes:
 * - Layout/typography mirror `app/not-found.tsx` for visual consistency.
 * - This component performs NO telemetry side effects. Reporting is owned by the
 *   boundary that caught the error, which keeps this component safe to render even
 *   when the surrounding app shell has already failed.
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
  digest,
  retryLabel = 'Try again',
  onRetry,
  secondaryLabel,
  onSecondary,
}) => {
  return (
    <div className="flex h-screen max-h-[80vh] items-center justify-center p-6">
      <div className="flex max-w-md flex-col items-center justify-center gap-4 text-center">
        <h1 className="typography-page-title text-semantic-text-primary">{title}</h1>
        <p className="typography-body text-semantic-text-secondary">{message}</p>

        {(onRetry || onSecondary) && (
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
            {onRetry && (
              <Button variant="primary" onClick={onRetry}>
                {retryLabel}
              </Button>
            )}
            {onSecondary && secondaryLabel && (
              <Button variant="secondary" onClick={onSecondary}>
                {secondaryLabel}
              </Button>
            )}
          </div>
        )}

        {digest && (
          <p className="mt-2 text-sm text-semantic-text-secondary opacity-70">
            Reference: {digest}
          </p>
        )}
      </div>
    </div>
  );
};

ErrorState.displayName = 'ErrorState';

export default ErrorState;
